"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useFormBranchContext } from "@/features/auth"
import { membersApi } from "@/features/members/services/members-api"
import {
  type GetMemberRelationshipsResponse,
  type Member,
  type MemberAdminUpdateInput,
  type MemberOnboardingInput,
  type MemberProfileUpdateInput,
  type RelationshipDecision,
} from "@/features/members/model/members-types"
import { type ListMembersQuery, type ListMembersResponse, type MembersOverviewResponse } from "@/features/members/services/members-api"
import { OverviewContext, OverviewGranularity } from "@ibada/shared-types/overview"

const MEMBERS_QUERY_KEY = ["members"] as const
const MEMBER_INVITES_QUERY_KEY = ["members", "invites"] as const
const MEMBERS_OVERVIEW_QUERY_KEY = ["members", "overview"] as const
const MEMBER_RELATIONSHIPS_QUERY_KEY = ["members", "relationships"] as const
// The owner setup checklist ("Register your first member") reads a separate dashboard query. We invalidate
// it by literal key so registering a member refreshes the checklist right away, without importing the
// dashboard feature's keys (mirrors workspaceSetupProgressKeys.all).
const WORKSPACE_SETUP_PROGRESS_KEY = ["dashboard", "workspace-setup-progress"] as const

export const membersQueryKeys = {
  all: MEMBERS_QUERY_KEY,
  list: (query?: ListMembersQuery, resolvedBranchId?: string) =>
    [
      ...MEMBERS_QUERY_KEY,
      query?.workspaceId ?? "__workspace__",
      resolvedBranchId ?? "__branch__",
      (query?.q ?? query?.search)?.trim() || "__all__",
      query?.segment ?? "__segment__",
      query?.page ?? "__page__",
      query?.limit ?? "__limit__",
      query?.sortBy ?? "__sortBy__",
      query?.sortOrder ?? "__sortOrder__",
      query?.filters ?? "__filters__",
    ] as const,
}

function invalidateMembersServerState(queryClient: ReturnType<typeof useQueryClient>, memberId?: string) {
  void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY })
  void queryClient.invalidateQueries({ queryKey: MEMBERS_OVERVIEW_QUERY_KEY })
  void queryClient.invalidateQueries({ queryKey: MEMBER_INVITES_QUERY_KEY })
  void queryClient.invalidateQueries({ queryKey: WORKSPACE_SETUP_PROGRESS_KEY })
  if (memberId) {
    void queryClient.invalidateQueries({ queryKey: ["member", memberId] })
    void queryClient.invalidateQueries({ queryKey: [...MEMBER_RELATIONSHIPS_QUERY_KEY, memberId] })
  }
}

/*
 * Both list hooks wait for a resolved branch.
 *
 * `activeBranchId` is null only while branch context is still resolving — parish scope resolves to the
 * SYSTEM branch's real id, so null never means "the whole workspace". Firing anyway sent `branchId:
 * undefined`, which the server answers with EVERY member, and that unscoped answer was then cached under the
 * key's `__branch__` placeholder — a single bucket shared by every branch. Opening a branch showed that
 * borrowed list for a moment before its own (often empty) result replaced it.
 */
export function useMembersListQuery(query?: ListMembersQuery) {
  const { activeBranchId } = useFormBranchContext()
  const resolvedBranchId = query?.branchId ?? activeBranchId ?? undefined
  return useQuery<ListMembersResponse>({
    queryKey: membersQueryKeys.list(query, resolvedBranchId),
    queryFn: () => membersApi.listMembers({ ...query, branchId: resolvedBranchId }),
    enabled: Boolean(resolvedBranchId),
  })
}

export function useMembersQuery(query?: ListMembersQuery) {
  const { activeBranchId } = useFormBranchContext()
  const resolvedBranchId = query?.branchId ?? activeBranchId ?? undefined
  return useQuery<ListMembersResponse, Error, Member[]>({
    queryKey: membersQueryKeys.list(query, resolvedBranchId),
    queryFn: () => membersApi.listMembers({ ...query, branchId: resolvedBranchId }),
    select: (data) => data.items,
    enabled: Boolean(resolvedBranchId),
  })
}

export type MembersOverviewQueryArgs = {
  workspaceId: string | null | undefined
  branchId?: string
  context?: OverviewContext
  dateFrom: string | undefined
  dateTo: string | undefined
  granularity?: OverviewGranularity
}

function useMembersOverviewQuery(args: MembersOverviewQueryArgs) {
  const workspaceIdKey = args.workspaceId ?? "__workspace__"
  const branchIdKey = args.branchId ?? "__branch__"
  const fromKey = args.dateFrom ?? "__from__"
  const toKey = args.dateTo ?? "__to__"
  const granularityKey = args.granularity ?? "__granularity__"

  const ready = Boolean(args.workspaceId && args.dateFrom && args.dateTo)

  return useQuery<MembersOverviewResponse>({
    queryKey: [...MEMBERS_OVERVIEW_QUERY_KEY, workspaceIdKey, branchIdKey, fromKey, toKey, granularityKey],
    queryFn: () =>
      membersApi.getMembersOverview({
        workspaceId: args.workspaceId!,
        branchId: args.branchId,
        context: args.context,
        dateFrom: args.dateFrom,
        dateTo: args.dateTo,
        granularity: args.granularity,
      }),
    enabled: ready,
  })
}

function useMemberQuery(id: string) {
  return useQuery<Member | null>({
    queryKey: ["member", id],
    queryFn: async () => {
      const member = await membersApi.getMemberById(id)
      return member ?? null
    },
    enabled: Boolean(id),
  })
}

function useMemberRelationshipsQuery(memberId: string) {
  return useQuery<GetMemberRelationshipsResponse>({
    queryKey: [...MEMBER_RELATIONSHIPS_QUERY_KEY, memberId],
    queryFn: () => membersApi.getMemberRelationships(memberId),
    enabled: Boolean(memberId),
  })
}

/** Confirms, dismisses or reopens one suggestion; the member's relationships are fetched again. */
function useDecideRelationshipMutation(memberId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ suggestionId, decision }: { suggestionId: string; decision: RelationshipDecision }) =>
      membersApi.decideRelationship(memberId, suggestionId, decision),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...MEMBER_RELATIONSHIPS_QUERY_KEY, memberId] })
    },
  })
}

function useMemberDocumentsQuery(memberId: string) {
  return useQuery({
    queryKey: ["member-documents", memberId],
    queryFn: () => membersApi.getMemberDocuments(memberId),
    enabled: Boolean(memberId),
  })
}

function useMemberInvitesQuery() {
  return useQuery({
    queryKey: MEMBER_INVITES_QUERY_KEY,
    queryFn: membersApi.listInvites,
  })
}

function useUpdateMemberMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Member> }) =>
      membersApi.updateMember(id, patch),
    onSuccess: (_member, variables) => {
      invalidateMembersServerState(queryClient, variables.id)
    },
  })
}

function useUpdateMemberProfileMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string
      patch: MemberProfileUpdateInput
    }) => membersApi.updateMemberProfile(id, patch),
    onSuccess: (_member, variables) => {
      invalidateMembersServerState(queryClient, variables.id)
    },
  })
}

function useUpdateMemberAdminMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string
      patch: Partial<MemberAdminUpdateInput>
    }) => membersApi.updateMemberAdmin(id, patch),
    onSuccess: (_member, variables) => {
      invalidateMembersServerState(queryClient, variables.id)
    },
  })
}

function useCreateMemberFromOnboardingMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: MemberOnboardingInput) =>
      membersApi.createMemberFromOnboarding(input),
    onSuccess: () => {
      invalidateMembersServerState(queryClient)
    },
  })
}

function useCreateMemberInviteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      memberId,
      email,
      actor,
    }: {
      memberId: string
      email: string
      actor: string
    }) => membersApi.createInvite(memberId, email, actor),
    onSuccess: () => {
      invalidateMembersServerState(queryClient)
    },
  })
}

function useBulkUpdateMembersMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, patch }: { ids: string[]; patch: Partial<Member> }) =>
      membersApi.updateMembers(ids, patch),
    onSuccess: (_members, variables) => {
      invalidateMembersServerState(queryClient)
      for (const id of variables.ids) {
        void queryClient.invalidateQueries({ queryKey: ["member", id] })
      }
    },
  })
}

function useDeleteMemberMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => membersApi.deleteMember(id),
    onSuccess: (_deleted, id) => {
      invalidateMembersServerState(queryClient, id)
    },
  })
}

export function useMembersFeatureQuery() {
  return {
    useMembersQuery,
    useMembersListQuery,
    useMembersOverviewQuery,
    useMemberQuery,
    useMemberRelationshipsQuery,
    useDecideRelationshipMutation,
    useMemberDocumentsQuery,
    useMemberInvitesQuery,
    useUpdateMemberMutation,
    useUpdateMemberProfileMutation,
    useUpdateMemberAdminMutation,
    useCreateMemberFromOnboardingMutation,
    useCreateMemberInviteMutation,
    useBulkUpdateMembersMutation,
    useDeleteMemberMutation,
  }
}



export {
  useMembersOverviewQuery,
  useMemberQuery,
  useMemberRelationshipsQuery,
  useDecideRelationshipMutation,
  useMemberDocumentsQuery,
  useMemberInvitesQuery,
  useUpdateMemberMutation,
  useUpdateMemberProfileMutation,
  useUpdateMemberAdminMutation,
  useCreateMemberFromOnboardingMutation,
  useCreateMemberInviteMutation,
  useBulkUpdateMembersMutation,
  useDeleteMemberMutation,
}
